import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {CommonInput, CommonLabel, FlexBox, INPUT_TYPES, useBreakpoint, useToasts } from '@brg/ui';
import {  API, Endpoints, Lang, TeamAPI, Team, ProjectAPI } from '@brg/api';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useModal } from 'core';
import { FieldTitle, ModalButtonFilled, ModalButtonHollow, RadioContainer, RepostedText, Subtitle, Title, Wrapper } from './styles';
import { useProfile } from '@brg/auth';
import { getFindTab, OptionActiveT, OptionWithAddT, REPOST_MODE, RepostTabs } from './const';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectsGridActions } from 'shared/reducers/ProjectsGridSlice';
import { useGetProject } from 'shared/hooks/api/useGetProject';
import userChannelSlice from 'shared/reducers/UserChannelSlice';
import RadioButton from 'shared/components/Radio';
import { ITeamTab } from 'shared/pages/teams/components/TeamTabEdit';
import { ModerationType } from 'shared/pages/teams/const/ModerationTypes';
import { useRouteMatch } from 'react-router-dom';
import { useChannelsContext } from 'shared/contexts/ChannelsContext';
import { TeamStatusDrafts } from 'shared/helpers/teamsStatus';
import { ActiveRepostsList } from './component/ActiveRepostsList';
import { allowedRepostItems } from './helpers/allowedRepostItems';
import { generateTabFilter, MAIN_FILTER_REPOST_PATH, normalizedTab } from './helpers/normalizedTab';
import {
    addProjectToOrganization,
    hasOrganizationTabs,
    isProjectInOrganization,
    isProjectInOrganizationTarget,
    removeProjectFromOrganization,
} from './helpers/repostProjectIds';

/**
 * @param Модалка репоста на канал @Artem
 */

export const RepostModal: FC<{
    projectId: string,
    callBack?: (flag?: boolean) => void,
    mode: REPOST_MODE
}> = ({
    projectId, 
    callBack,
    mode = REPOST_MODE.add
}) => {
    const { t, i18n: { language } } = useTranslation();
    const { info, error } = useToasts();
    const theme = useTheme();
    const profile = useProfile();
    const modal = useModal();
    const dispatch = useDispatch();
    //@ts-ignore
    const currentTeam = useSelector((state) => state?.organizations)?.team;
    const [time,setTime] = useState(Date.now());
    const {project} = useGetProject(projectId, time);
    const [activeTab, setActiveTab] = useState<RepostTabs>(RepostTabs.feed);
    const [selectedTabs, setSelectedTabs] = useState<OptionWithAddT[]>();
    const [tabsRemoved, setTabsRemoved] = useState<boolean>(false);

    const { 
        channels, 
        loading, 
        refetch,
        reset 
    } = useChannelsContext();

    const computeChannels = useMemo(
        () => allowedRepostItems(channels || [], profile)
    , [profile, channels])

    const hasOriginalValue = useMemo(() => {
        if (loading) return false;
        return getFindTab(computeChannels || [], projectId)
    } , [computeChannels, projectId, loading])

    const projectOnFeedOrChannel = useMemo(
        () => (!!project?.isInSub || !!hasOriginalValue ) 
    , [project, hasOriginalValue])

    const isOwner = useMemo(
        () => (currentTeam && profile && currentTeam?.ownersIDs?.includes(profile.accountId)) 
    , [profile, selectedTabs])

    const isChannel = useMemo(
        () =>  {
            return [/*ModerationType.Channel,*/ ModerationType.ChannelGroupOrCourse]?.includes(currentTeam?.moderationType)
        }
    , [currentTeam, selectedTabs])

    const shouldUpdateTeamState = useMemo(() => {
        return /*isChannel &&*/ isOwner
    } , [isChannel, isOwner])

    const feedOption = useMemo(() => {
        return {
            label: t('repost.modal.feed_title'),
            value: 'feed',
            addOptions: []
        }
    } , [language])

    const placeholderOption = useMemo(() => {
        return {
            label: t('repost.modal.default_option'),
            value: ''
        }
    } , [language])

    const computeTabs = useMemo(() => {
        const tabs = computeChannels?.reduce<OptionWithAddT[]>((acc, channel) => {
            const channelHasTabs = hasOrganizationTabs(channel);

            return [
                ...acc,
                {
                    label: Lang(channel.name, language),
                    value: channel.teamId,
                    addOptions: channelHasTabs
                        ? normalizedTab(channel, channel.moderationType as ModerationType, language)
                        : [],
                },
            ];
        }, []);

        return [feedOption, ...tabs || []];
    }, [computeChannels, language, time, feedOption]);

    const submitText = useMemo(() => {
        return t('repost.modal.buttons.apply')
    }, [computeTabs, project, activeTab, projectOnFeedOrChannel])

    useEffect(() => {
        return () => {
            dispatch(userChannelSlice.actions.reset())
        }
    } , [])

    const onCancel = () => {
        modal.hide();
    }

    const onUpdateLocalState = (team: Team<'Global'>) => {
        dispatch(ProjectsGridActions.setTabs({
            tabs: team?.tabs,
            //@ts-ignore
            categories: team?.categories || [],
            mainEntityType: team.mainEntityType,
            mainFilter: team.mainFilter,
        }))
    }

    const handleUpdateFeed = async(isDeleted?: boolean) => {
        if (!project) return;

        await API.request()
        .endpoint(Endpoints.subscriptions)
        .url(`/api/Subscription/${isDeleted ? 'CancelSubscription' : 'Subscribe'}`)
        .post()
        .body({
            subscriberId: profile?.accountId,
            subscriberType: 0,
            targetId: projectId,
            targetType: 'project',
        })
        .send()
        .then((response) => {
            if (response.status === 200) {
                info(t('repost.modal.msg.feed_update'));
            }

            if (shouldUpdateTeamState && isDeleted) {
                window.location.reload();
                // window.location.href = window.origin + '/teams/' + currentTeam.teamId
            }
            callBack?.(isDeleted ? false : true)
        })
    }

    const handleUpdateTeamTabs = async (data: Team<'Global'>[], closable?: boolean, message?: string) => {
        Promise.all(data.map(async (team) => {
            return await TeamAPI.Update(team)
                .then((response) => {
                    if (response.status === 200) {
                        refetch?.()
                        if (shouldUpdateTeamState) {
                            onUpdateLocalState(team);
                        }
    
                        callBack?.(isProjectInOrganization(team, projectId))
    
                        if (closable) {
                            return modal.hide()
                        }
                    }
                })

        })).then(() => {})
            if (message) {
                info(message)
            }
    }


    const isActive = useCallback((levelOneId: string, levelTwoId?: string) => {
        const channel = computeChannels?.find((item) => item?.teamId === levelOneId);

        if (!channel) {
            return false;
        }

        if (!hasOrganizationTabs(channel)) {
            const hasProject = isProjectInOrganization(channel, projectId);
            const isSelected = selectedTabs?.some((item) => item.value === levelOneId);

            if (hasProject || isSelected) {
                return levelOneId;
            }

            return false;
        }

        if (!levelTwoId) {
            return false;
        }

        const hasProject = isProjectInOrganizationTarget(channel, projectId, levelTwoId);
        const isSelected = selectedTabs?.find((item) =>
            item.value === levelOneId && item?.addOptions?.[0]?.value === levelTwoId,
        );

        if (hasProject || isSelected) {
            return levelTwoId;
        }

        return false;
    }, [computeChannels, projectId, selectedTabs]);


    const disableSubmit = useMemo(() => {
        return !selectedTabs?.length && !tabsRemoved
    } , [selectedTabs, activeTab, tabsRemoved])

    const onSubmit = () => {
        if (disableSubmit) return;

        const feed = selectedTabs?.find((item) => item?.value === 'feed');

        const teamsToUpdate = computeChannels?.reduce<Team<'Global'>[]>((acc, channel) => {
            const selectedForChannel = selectedTabs?.filter((item) => item.value === channel.teamId);

            if (!selectedForChannel?.length) {
                return acc;
            }

            const updatedChannel = selectedForChannel.reduce((currentChannel, selected) => {
                const targetPath = selected.addOptions?.[0]?.value;

                if (!targetPath) {
                    return currentChannel;
                }

                return addProjectToOrganization(currentChannel, projectId, targetPath);
            }, channel);

            return [...acc, updatedChannel];
        }, []) || [];

        if (feed) {
            handleUpdateFeed().then(() => modal.hide());
        }

        if (teamsToUpdate.length) {
            const teams = teamsToUpdate;
            const closable = true;
            const message = t('repost.modal.msg.channel_update')
            handleUpdateTeamTabs(teams, closable, message)

        } 
        else if (tabsRemoved) {
            modal.hide();
        }
    };

    const onRemove = useCallback((id: string, parentId: string) => {
        const isSelected = selectedTabs?.find((tab) =>{
            return tab?.addOptions?.[0]?.value === id || tab?.value === id
        }) 
        const isFeed = id === 'feed';

        if (isSelected) {
            setSelectedTabs(selectedTabs?.filter((item) => {
                if (isFeed) {
                    return item?.value !== 'feed'
                }
                return item?.addOptions?.[0]?.value !== id
            }))
            return;
        } 

        const channel = computeChannels?.find((channel) => {
            return channel?.teamId === parentId
        })

        if (isFeed) {
            handleUpdateFeed(true).then(() => {
                setTime(Date.now());
            });
            return;
        }

        if (channel) {
            const teams = [removeProjectFromOrganization(channel, projectId, id)]
            const closable = false;
            const message = t('repost.modal.msg.channel_update')
            handleUpdateTeamTabs(teams, closable, message).then(() => setTime(Date.now()))

        }

        setTabsRemoved(true);
    }, [selectedTabs, computeChannels, project, refetch, projectId])

    const activeOptions: OptionActiveT[] = useMemo(() => {
        const filteredOptions = computeTabs?.filter((item) => {
            if (item.value === 'feed' && (!!selectedTabs?.find((i) => i.value === 'feed') || !!project?.isInSub)) {
                return true;
            }

            const channel = computeChannels?.find((org) => org.teamId === item.value);

            if (channel && !hasOrganizationTabs(channel)) {
                return !!isActive(item.value);
            }

            return item?.addOptions?.some((option) => !!isActive(item.value, option.value));
        }) as OptionWithAddT[];

        const calcOptions = filteredOptions?.map((item) => {
            const channel = computeChannels?.find((org) => org.teamId === item.value);

            if (channel && !hasOrganizationTabs(channel)) {
                return {
                    ...item,
                    addOptions: isActive(item.value)
                        ? [{
                            label: item.label,
                            value: MAIN_FILTER_REPOST_PATH,
                        }]
                        : [],
                };
            }

            return {
                ...item,
                addOptions: item?.addOptions?.filter((option) => !!isActive(item.value, option?.value)) || [],
            };
        });

        return [...calcOptions || []].reduce<OptionActiveT[]>((acc, item) => {
            const projectOnFeed = !!project?.isInSub || selectedTabs?.find((i) => i.value === 'feed');
            const channel = computeChannels?.find((org) => org.teamId === item.value);
            const isMainFilterOnly = channel && !hasOrganizationTabs(channel);

            const transformedOptions = item.value === 'feed' && projectOnFeed
                ? [{
                    label: item.label,
                    value: item.value,
                    parent: null,
                }]
                : isMainFilterOnly
                    ? item.addOptions.map((option) => ({
                        label: item.label,
                        value: option.value,
                        parent: {
                            label: item.label,
                            value: item.value,
                        },
                    }))
                    : item.addOptions.map((option) => ({
                        label: option.label,
                        value: option.value,
                        parent: {
                            label: item.label,
                            value: item.value,
                        },
                    }));

            acc.push(...transformedOptions);
            return acc;
        }, []);
    }, [computeTabs, selectedTabs, project, time, computeChannels, isActive]);


    const onChange = useCallback((channelId: string, tabId: string) => {
        const channel = computeChannels?.find((item) => item?.teamId === channelId);
        const channelHasTabs = channel && hasOrganizationTabs(channel);
        const tab = channel ? generateTabFilter(channel, tabId || MAIN_FILTER_REPOST_PATH, language) : undefined;
        let newTab;

        const isSelected = !!selectedTabs?.find((item) => item.value === channelId)
            || !!activeOptions?.find(
                (item) => item?.value === tabId || item?.value === channelId,
            );

        if (isSelected) {
            return;
        }

        if (channel) {
            newTab = {
                label: Lang(channel.name, language),
                value: channel.teamId,
                addOptions: [
                    {
                        label: channelHasTabs ? Lang(tab?.name, language) : Lang(channel.name, language),
                        value: channelHasTabs ? tab?.path : MAIN_FILTER_REPOST_PATH,
                    },
                ],
            };
        }

        if (channelId === 'feed') {
            newTab = {
                label: t('repost.modal.feed_title'),
                value: 'feed',
                addOptions: [
                    {
                        label: t('repost.modal.feed_title'),
                        value: 'feed',
                    },
                ],
            };
        }

        if (newTab) {
            setSelectedTabs([...selectedTabs || [], newTab]);
        }

    }, [selectedTabs, computeChannels, language, activeOptions, t]);

    const channelsList = useMemo(() => {

        return (
            <FlexBox direction='column'>
                <FieldTitle color={theme.approvalBlockStyles.color}>{t('repost.modal.channels_title')}</FieldTitle>
                <CommonInput 
                    type={INPUT_TYPES.level_dropdown}
                    orientation={'column'}
                    maxHeight={250}
                    placeholder={t('repost.modal.channels_placeholder')}
                    value={placeholderOption}
                    options={computeTabs as any}
                    isActive={(id1: string, id2?: string) => isActive(id1, id2) as string}
                    //@ts-ignore
                    onChange={onChange}
                />
            </FlexBox>
        )
    } , [computeTabs, activeTab, selectedTabs, feedOption, placeholderOption, language])

    const activeReposts = useMemo(() => {
        if (activeOptions?.length) {
            return (
                <ActiveRepostsList 
                    array={activeOptions} 
                    onRemove={onRemove}
                />
            )
        }
    } , [activeOptions])

    const title = useMemo(() => {

        if (projectOnFeedOrChannel) {
            return (
                <Title color={theme.not_saved_modal.title}>
                {t('repost.modal.move')}
            </Title> 
            )
        }
        return (
            <Title color={theme.not_saved_modal.title}>
                {t('repost.modal.title')}
            </Title>
        )
    } , [mode, projectOnFeedOrChannel])

    useEffect(() => {
        return () => {
            reset?.()
        }
    } , [])


    return <Wrapper direction={'column'} width={'100%'} justify='left' align='center' gy={32}>
            {title}
            {channelsList}
            {activeReposts}
            <FlexBox width={'100%'} direction='row' directionMobile='column' gx={24} gxMobile={1} gy={1} gyMobile={8}>
                <ModalButtonHollow 
                    onClick={onCancel}
                >
                    {t('repost.modal.buttons.cancel')}
                </ModalButtonHollow>
                <ModalButtonFilled 
                    disabled={disableSubmit} 
                    onClick={onSubmit}
                >
                    {submitText}
                </ModalButtonFilled>
            </FlexBox>
        </Wrapper>

};
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {CommonInput, CommonLabel, FlexBox, INPUT_TYPES, useBreakpoint, useToasts } from '@brg/ui';
import {  API, Endpoints, Lang, TeamAPI, Team, ProjectAPI } from '@brg/api';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useModal } from 'core';
import { FieldTitle, ModalButtonFilled, ModalButtonHollow, RadioContainer, RepostedText, Subtitle, Title, Wrapper } from './styles';
import { useProfile } from '@brg/auth';
import { getFindTab, OptionActiveT, OptionWithAddT, REPOST_MODE, RepostTabs } from './const';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectsGridActions } from 'shared/reducers/ProjectsGridSlice';
import { useGetProject } from 'shared/hooks/api/useGetProject';
import userChannelSlice from 'shared/reducers/UserChannelSlice';
import RadioButton from 'shared/components/Radio';
import { ITeamTab } from 'shared/pages/teams/components/TeamTabEdit';
import { ModerationType } from 'shared/pages/teams/const/ModerationTypes';
import { useRouteMatch } from 'react-router-dom';
import { useChannelsContext } from 'shared/contexts/ChannelsContext';
import { TeamStatusDrafts } from 'shared/helpers/teamsStatus';
import { ActiveRepostsList } from './component/ActiveRepostsList';
import { allowedRepostItems } from './helpers/allowedRepostItems';
import { generateTabFilter, MAIN_FILTER_REPOST_PATH, normalizedTab } from './helpers/normalizedTab';
import {
    addProjectToOrganization,
    hasOrganizationTabs,
    isProjectInOrganization,
    isProjectInOrganizationTarget,
    removeProjectFromOrganization,
} from './helpers/repostProjectIds';

/**
 * @param Модалка репоста на канал @Artem
 */

export const RepostModal: FC<{
    projectId: string,
    callBack?: (flag?: boolean) => void,
    mode: REPOST_MODE
}> = ({
    projectId, 
    callBack,
    mode = REPOST_MODE.add
}) => {
    const { t, i18n: { language } } = useTranslation();
    const { info, error } = useToasts();
    const theme = useTheme();
    const profile = useProfile();
    const modal = useModal();
    const dispatch = useDispatch();
    //@ts-ignore
    const currentTeam = useSelector((state) => state?.organizations)?.team;
    const [time,setTime] = useState(Date.now());
    const {project} = useGetProject(projectId, time);
    const [activeTab, setActiveTab] = useState<RepostTabs>(RepostTabs.feed);
    const [selectedTabs, setSelectedTabs] = useState<OptionWithAddT[]>();
    const [tabsRemoved, setTabsRemoved] = useState<boolean>(false);

    const { 
        channels, 
        loading, 
        refetch,
        reset 
    } = useChannelsContext();

    const computeChannels = useMemo(
        () => allowedRepostItems(channels || [], profile)
    , [profile, channels])

    const hasOriginalValue = useMemo(() => {
        if (loading) return false;
        return getFindTab(computeChannels || [], projectId)
    } , [computeChannels, projectId, loading])

    const projectOnFeedOrChannel = useMemo(
        () => (!!project?.isInSub || !!hasOriginalValue ) 
    , [project, hasOriginalValue])

    const isOwner = useMemo(
        () => (currentTeam && profile && currentTeam?.ownersIDs?.includes(profile.accountId)) 
    , [profile, selectedTabs])

    const isChannel = useMemo(
        () =>  {
            return [/*ModerationType.Channel,*/ ModerationType.ChannelGroupOrCourse]?.includes(currentTeam?.moderationType)
        }
    , [currentTeam, selectedTabs])

    const shouldUpdateTeamState = useMemo(() => {
        return /*isChannel &&*/ isOwner
    } , [isChannel, isOwner])

    const feedOption = useMemo(() => {
        return {
            label: t('repost.modal.feed_title'),
            value: 'feed',
            addOptions: []
        }
    } , [language])

    const placeholderOption = useMemo(() => {
        return {
            label: t('repost.modal.default_option'),
            value: ''
        }
    } , [language])

    const computeTabs = useMemo(() => {
        const tabs = computeChannels?.reduce<OptionWithAddT[]>((acc, channel) => {
            const channelHasTabs = hasOrganizationTabs(channel);

            return [
                ...acc,
                {
                    label: Lang(channel.name, language),
                    value: channel.teamId,
                    addOptions: channelHasTabs
                        ? normalizedTab(channel, channel.moderationType as ModerationType, language)
                        : [],
                },
            ];
        }, []);

        return [feedOption, ...tabs || []];
    }, [computeChannels, language, time, feedOption]);

    const submitText = useMemo(() => {
        return t('repost.modal.buttons.apply')
    }, [computeTabs, project, activeTab, projectOnFeedOrChannel])

    useEffect(() => {
        return () => {
            dispatch(userChannelSlice.actions.reset())
        }
    } , [])

    const onCancel = () => {
        modal.hide();
    }

    const onUpdateLocalState = (team: Team<'Global'>) => {
        dispatch(ProjectsGridActions.setTabs({
            tabs: team?.tabs,
            //@ts-ignore
            categories: team?.categories || [],
            mainEntityType: team.mainEntityType,
            mainFilter: team.mainFilter,
        }))
    }

    const handleUpdateFeed = async(isDeleted?: boolean) => {
        if (!project) return;

        await API.request()
        .endpoint(Endpoints.subscriptions)
        .url(`/api/Subscription/${isDeleted ? 'CancelSubscription' : 'Subscribe'}`)
        .post()
        .body({
            subscriberId: profile?.accountId,
            subscriberType: 0,
            targetId: projectId,
            targetType: 'project',
        })
        .send()
        .then((response) => {
            if (response.status === 200) {
                info(t('repost.modal.msg.feed_update'));
            }

            if (shouldUpdateTeamState && isDeleted) {
                window.location.reload();
                // window.location.href = window.origin + '/teams/' + currentTeam.teamId
            }
            callBack?.(isDeleted ? false : true)
        })
    }

    const handleUpdateTeamTabs = async (data: Team<'Global'>[], closable?: boolean, message?: string) => {
        Promise.all(data.map(async (team) => {
            return await TeamAPI.Update(team)
                .then((response) => {
                    if (response.status === 200) {
                        refetch?.()
                        if (shouldUpdateTeamState) {
                            onUpdateLocalState(team);
                        }
    
                        callBack?.(isProjectInOrganization(team, projectId))
    
                        if (closable) {
                            return modal.hide()
                        }
                    }
                })

        })).then(() => {})
            if (message) {
                info(message)
            }
    }


    const isActive = useCallback((levelOneId: string, levelTwoId?: string) => {
        const channel = computeChannels?.find((item) => item?.teamId === levelOneId);

        if (!channel) {
            return false;
        }

        if (!hasOrganizationTabs(channel)) {
            const hasProject = isProjectInOrganization(channel, projectId);
            const isSelected = selectedTabs?.some((item) => item.value === levelOneId);

            if (hasProject || isSelected) {
                return levelOneId;
            }

            return false;
        }

        if (!levelTwoId) {
            return false;
        }

        const hasProject = isProjectInOrganizationTarget(channel, projectId, levelTwoId);
        const isSelected = selectedTabs?.find((item) =>
            item.value === levelOneId && item?.addOptions?.[0]?.value === levelTwoId,
        );

        if (hasProject || isSelected) {
            return levelTwoId;
        }

        return false;
    }, [computeChannels, projectId, selectedTabs]);


    const disableSubmit = useMemo(() => {
        return !selectedTabs?.length && !tabsRemoved
    } , [selectedTabs, activeTab, tabsRemoved])

    const onSubmit = () => {
        if (disableSubmit) return;

        const feed = selectedTabs?.find((item) => item?.value === 'feed');

        const teamsToUpdate = computeChannels?.reduce<Team<'Global'>[]>((acc, channel) => {
            const selectedForChannel = selectedTabs?.filter((item) => item.value === channel.teamId);

            if (!selectedForChannel?.length) {
                return acc;
            }

            const updatedChannel = selectedForChannel.reduce((currentChannel, selected) => {
                const targetPath = selected.addOptions?.[0]?.value;

                if (!targetPath) {
                    return currentChannel;
                }

                return addProjectToOrganization(currentChannel, projectId, targetPath);
            }, channel);

            return [...acc, updatedChannel];
        }, []) || [];

        if (feed) {
            handleUpdateFeed().then(() => modal.hide());
        }

        if (teamsToUpdate.length) {
            const teams = teamsToUpdate;
            const closable = true;
            const message = t('repost.modal.msg.channel_update')
            handleUpdateTeamTabs(teams, closable, message)

        } 
        else if (tabsRemoved) {
            modal.hide();
        }
    };

    const onRemove = useCallback((id: string, parentId: string) => {
        const isSelected = selectedTabs?.find((tab) =>{
            return tab?.addOptions?.[0]?.value === id || tab?.value === id
        }) 
        const isFeed = id === 'feed';

        if (isSelected) {
            setSelectedTabs(selectedTabs?.filter((item) => {
                if (isFeed) {
                    return item?.value !== 'feed'
                }
                return item?.addOptions?.[0]?.value !== id
            }))
            return;
        } 

        const channel = computeChannels?.find((channel) => {
            return channel?.teamId === parentId
        })

        if (isFeed) {
            handleUpdateFeed(true).then(() => {
                setTime(Date.now());
            });
            return;
        }

        if (channel) {
            const teams = [removeProjectFromOrganization(channel, projectId, id)]
            const closable = false;
            const message = t('repost.modal.msg.channel_update')
            handleUpdateTeamTabs(teams, closable, message).then(() => setTime(Date.now()))

        }

        setTabsRemoved(true);
    }, [selectedTabs, computeChannels, project, refetch, projectId])

    const activeOptions: OptionActiveT[] = useMemo(() => {
        const filteredOptions = computeTabs?.filter((item) => {
            if (item.value === 'feed' && (!!selectedTabs?.find((i) => i.value === 'feed') || !!project?.isInSub)) {
                return true;
            }

            const channel = computeChannels?.find((org) => org.teamId === item.value);

            if (channel && !hasOrganizationTabs(channel)) {
                return !!isActive(item.value);
            }

            return item?.addOptions?.some((option) => !!isActive(item.value, option.value));
        }) as OptionWithAddT[];

        const calcOptions = filteredOptions?.map((item) => {
            const channel = computeChannels?.find((org) => org.teamId === item.value);

            if (channel && !hasOrganizationTabs(channel)) {
                return {
                    ...item,
                    addOptions: isActive(item.value)
                        ? [{
                            label: item.label,
                            value: MAIN_FILTER_REPOST_PATH,
                        }]
                        : [],
                };
            }

            return {
                ...item,
                addOptions: item?.addOptions?.filter((option) => !!isActive(item.value, option?.value)) || [],
            };
        });

        return [...calcOptions || []].reduce<OptionActiveT[]>((acc, item) => {
            const projectOnFeed = !!project?.isInSub || selectedTabs?.find((i) => i.value === 'feed');
            const channel = computeChannels?.find((org) => org.teamId === item.value);
            const isMainFilterOnly = channel && !hasOrganizationTabs(channel);

            const transformedOptions = item.value === 'feed' && projectOnFeed
                ? [{
                    label: item.label,
                    value: item.value,
                    parent: null,
                }]
                : isMainFilterOnly
                    ? item.addOptions.map((option) => ({
                        label: item.label,
                        value: option.value,
                        parent: {
                            label: item.label,
                            value: item.value,
                        },
                    }))
                    : item.addOptions.map((option) => ({
                        label: option.label,
                        value: option.value,
                        parent: {
                            label: item.label,
                            value: item.value,
                        },
                    }));

            acc.push(...transformedOptions);
            return acc;
        }, []);
    }, [computeTabs, selectedTabs, project, time, computeChannels, isActive]);


    const onChange = useCallback((channelId: string, tabId: string) => {
        const channel = computeChannels?.find((item) => item?.teamId === channelId);
        const channelHasTabs = channel && hasOrganizationTabs(channel);
        const tab = channel ? generateTabFilter(channel, tabId || MAIN_FILTER_REPOST_PATH, language) : undefined;
        let newTab;

        const isSelected = !!selectedTabs?.find((item) => item.value === channelId)
            || !!activeOptions?.find(
                (item) => item?.value === tabId || item?.value === channelId,
            );

        if (isSelected) {
            return;
        }

        if (channel) {
            newTab = {
                label: Lang(channel.name, language),
                value: channel.teamId,
                addOptions: [
                    {
                        label: channelHasTabs ? Lang(tab?.name, language) : Lang(channel.name, language),
                        value: channelHasTabs ? tab?.path : MAIN_FILTER_REPOST_PATH,
                    },
                ],
            };
        }

        if (channelId === 'feed') {
            newTab = {
                label: t('repost.modal.feed_title'),
                value: 'feed',
                addOptions: [
                    {
                        label: t('repost.modal.feed_title'),
                        value: 'feed',
                    },
                ],
            };
        }

        if (newTab) {
            setSelectedTabs([...selectedTabs || [], newTab]);
        }

    }, [selectedTabs, computeChannels, language, activeOptions, t]);

    const channelsList = useMemo(() => {

        return (
            <FlexBox direction='column'>
                <FieldTitle color={theme.approvalBlockStyles.color}>{t('repost.modal.channels_title')}</FieldTitle>
                <CommonInput 
                    type={INPUT_TYPES.level_dropdown}
                    orientation={'column'}
                    maxHeight={250}
                    placeholder={t('repost.modal.channels_placeholder')}
                    value={placeholderOption}
                    options={computeTabs as any}
                    isActive={(id1: string, id2?: string) => isActive(id1, id2) as string}
                    //@ts-ignore
                    onChange={onChange}
                />
            </FlexBox>
        )
    } , [computeTabs, activeTab, selectedTabs, feedOption, placeholderOption, language])

    const activeReposts = useMemo(() => {
        if (activeOptions?.length) {
            return (
                <ActiveRepostsList 
                    array={activeOptions} 
                    onRemove={onRemove}
                />
            )
        }
    } , [activeOptions])

    const title = useMemo(() => {

        if (projectOnFeedOrChannel) {
            return (
                <Title color={theme.not_saved_modal.title}>
                {t('repost.modal.move')}
            </Title> 
            )
        }
        return (
            <Title color={theme.not_saved_modal.title}>
                {t('repost.modal.title')}
            </Title>
        )
    } , [mode, projectOnFeedOrChannel])

    useEffect(() => {
        return () => {
            reset?.()
        }
    } , [])


    return <Wrapper direction={'column'} width={'100%'} justify='left' align='center' gy={32}>
            {title}
            {channelsList}
            {activeReposts}
            <FlexBox width={'100%'} direction='row' directionMobile='column' gx={24} gxMobile={1} gy={1} gyMobile={8}>
                <ModalButtonHollow 
                    onClick={onCancel}
                >
                    {t('repost.modal.buttons.cancel')}
                </ModalButtonHollow>
                <ModalButtonFilled 
                    disabled={disableSubmit} 
                    onClick={onSubmit}
                >
                    {submitText}
                </ModalButtonFilled>
            </FlexBox>
        </Wrapper>

};
